import axios from "../../../config/axiosConfig";

const clienteServicioService = {
  /**
   * Obtiene los datos de un cliente-servicio por su código
   * @param {string|number} codigo - Código del cliente-servicio (ej: 866)
   * @returns {Promise<Object>} Datos del cliente-servicio
   */
  getClienteServicio: async (codigo) => {
    try {
      const response = await axios.get(`/api/cliente-servicio/${codigo}`);
      return response.data;
    } catch (error) {
      console.error(`Error al obtener cliente-servicio ${codigo}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene todos los clientes-servicio
   * @returns {Promise<Array>} Lista de clientes-servicio
   */
  getAllClientesServicio: async () => {
    try {
      const response = await axios.get("/api/cliente-servicio");
      return response.data;
    } catch (error) {
      console.error("Error al obtener clientes-servicio:", error);
      throw error;
    }
  },

  /**
   * Crea un nuevo cliente-servicio
   * @param {Object} clienteServicioData - Datos del nuevo cliente-servicio
   * @returns {Promise<Object>} Cliente-servicio creado
   */
  createClienteServicio: async (clienteServicioData) => {
    try {
      const response = await axios.post("/api/cliente-servicio", clienteServicioData);
      return response.data;
    } catch (error) {
      console.error("Error al crear cliente-servicio:", error);
      throw error;
    }
  },

  /**
   * Actualiza un cliente-servicio existente
   * @param {string|number} codigo - Código del cliente-servicio a actualizar
   * @param {Object} clienteServicioData - Datos actualizados
   * @returns {Promise<Object>} Cliente-servicio actualizado
   */
  updateClienteServicio: async (codigo, clienteServicioData) => {
    try {
      const response = await axios.put(`/api/cliente-servicio/${codigo}`, clienteServicioData);
      return response.data;
    } catch (error) {
      console.error(`Error al actualizar cliente-servicio ${codigo}:`, error);
      throw error;
    }
  },

  /**
   * Lista módems activos/vendidos activos (paginado en backend).
   * @param {string|number} cod_servicio
   * @param {Object} opts
   * @param {number} opts.page
   * @param {number} opts.limit
   * @returns {Promise<{items:Array, meta:Object}>}
   */
  getModemsActivos: async (cod_servicio, opts = {}) => {
    try {
      const response = await axios.get("/api/historial-cliente/modems-activos", {
        params: {
          cod_servicio,
          page: opts.page ?? 1,
          limit: opts.limit ?? 50,
          q: opts.q ?? "",
        },
      });
      return response.data; // { items, meta }
    } catch (error) {
      console.error("Error al obtener modems activos:", error);
      throw error;
    }
  },

  getModemsSoloActivos: async (cod_servicio, opts = {}) => {
    const response = await axios.get("/api/historial-cliente/modems-solo-activos", {
      params: {
        cod_servicio,
        page:  opts.page  ?? 1,
        limit: opts.limit ?? 50,
        q:     opts.q     ?? "",
      },
    });
    return response.data;
  },
  
  getHistorialCompletoModem: async (cod_servicio, opts = {}) => {
    const response = await axios.get("/api/historial-cliente/historial-completo", {
      params: {
        cod_servicio,
        page:  opts.page  ?? 1,
        limit: opts.limit ?? 50,
        q:     opts.q     ?? "",
      },
    });
    return response.data;
  },
  
  getHistorialMantenimientoModem: async (cod_servicio, opts = {}) => {
    const response = await axios.get("/api/historial-cliente/mantenimientos", {
      params: {
        cod_servicio,
        page:  opts.page  ?? 1,
        limit: opts.limit ?? 50,
        q:     opts.q     ?? "",
      },
    });
    return response.data;
  },
  
  // Mantenimientos de módems agrupados por requerimiento; desde/hasta (AAAA-MM-DD) filtran la fecha del REQ.
  // campo: "registro" (fecha del REQ) o "culminacion".
  getMantenimientosPorReq: async (cod_servicio, { desde, hasta, campo = "registro" } = {}) => {
    const response = await axios.get("/api/historial-cliente/mantenimientos-por-req", {
      params: { cod_servicio, desde, hasta, campo },
    });
    return response.data;
  },

  getDetalleOT: async (cod_servicio, opts = {}) => {
    const response = await axios.get("/api/historial-cliente/detalle-ot", {
      params: {
        cod_servicio,
        page:  opts.page  ?? 1,
        limit: opts.limit ?? 50,
        q:     opts.q     ?? "",
      },
    });
    return response.data;
  },

  getDetalleReq: async (cod_servicio, opts = {}) => {
    const response = await axios.get("/api/historial-cliente/detalle-req", {
      params: {
        cod_servicio,
        page:  opts.page  ?? 1,
        limit: opts.limit ?? 50,
        q:     opts.q     ?? "",
      },
    });
    return response.data;
  },

  getEquiposPorEstado: async (cod_servicio, opts = {}) => {
    const response = await axios.get("/api/historial-cliente/equipos-por-estado", {
      params: {
        cod_servicio,
        estado: opts.estado,
        dato:   opts.dato ?? 1,
        page:   opts.page  ?? 1,
        limit:  opts.limit ?? 50,
        q:      opts.q     ?? "",
      },
    });
    return response.data;
  },

  getResumenEquipo: async (cod_servicio) => {
    const response = await axios.get("/api/historial-cliente/resumen-equipo", {
      params: { cod_servicio },
    });
    return response.data;
  },

  getHistorialCompletoEquipo: async (cod_servicio, opts = {}) => {
    const response = await axios.get("/api/historial-cliente/historial-completo-equipo", {
      params: {
        cod_servicio,
        dato:  opts.dato  ?? 1,
        page:  opts.page  ?? 1,
        limit: opts.limit ?? 50,
        q:     opts.q     ?? "",
      },
    });
    return response.data;
  },

  getHistorialCompletoAccesorio: async (cod_servicio, opts = {}) => {
    const response = await axios.get("/api/historial-cliente/historial-completo-accesorio", {
      params: {
        cod_servicio,
        page:  opts.page  ?? 1,
        limit: opts.limit ?? 50,
        q:     opts.q     ?? "",
      },
    });
    return response.data;
  },

  getAccesoriosActualesCliente: async (cod_servicio) => {
    const response = await axios.get("/api/historial-cliente/accesorios-actuales", {
      params: { cod_servicio },
    });
    return response.data;
  },

  getServicioAdicional: async (cod_servicio, opts = {}) => {
    const response = await axios.get("/api/historial-cliente/adicional", {
      params: {
        cod_servicio,
        page:  opts.page  ?? 1,
        limit: opts.limit ?? 50,
        q:     opts.q     ?? "",
      },
    });
    return response.data;
  },

  getMantenimientosEquipo: async (cod_servicio, opts = {}) => {
  const response = await axios.get("/api/historial-cliente/mantenimientos-equipo", {
      params: {
        cod_servicio,
        dato:  opts.dato  ?? 1,
        page:  opts.page  ?? 1,
        limit: opts.limit ?? 50,
        q:     opts.q     ?? "",
      },
    });
    return response.data;
  },

  updateModemDatosAvl: async (cod_servicio, num_sim, payload = {}) => {
    const response = await axios.put(
      `/api/historial-cliente/servicios/${cod_servicio}/modems/${encodeURIComponent(num_sim)}/datos-avl`,
      payload
    );
    return response.data;
  },

  updateEquipoDatos: async (cod_servicio, tipo, serie, payload = {}) => {
    const response = await axios.put(
      `/api/historial-cliente/servicios/${cod_servicio}/equipos/${tipo}/${encodeURIComponent(serie)}/datos`,
      payload
    );
    return response.data;
  },

  getCoordenadasModem: async ({ num_sim, emai, plataforma }) => {
    const response = await axios.get("/api/modem-ultima-conexion/coordenadas", {
      params: { num_sim, emai, plataforma },
    });
    return response.data;
  },

};



export default clienteServicioService;
